import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';
import { parseStatusColorConfig } from '../../utils/statusColor';

export interface ClientService {
  clientServicetId: number;
  clientId?: number;
  serviceCreatedDate?: string;
  serviceType?: number;
  serviceCategory?: number;
  serviceDescr?: string;
  serviceStartDate?: string;
  serviceEndDate?: string;
  serviceTerminationDate?: string;
  serviceFreq?: number;
  serviceFreqType?: number;
  serviceActive?: boolean;
  serviceNotes?: string;
  serviceCommonNote?: string;
  serviceCost?: number;
  serviceInvoice?: number;
  serviceProfit?: number;
  serviceCreatedBy?: number;
  serviceUpdatedBy?: number;
  serviceCreatedAt?: string;
  serviceUpdatedAt?: string;
  // Related data from includes
  client?: {
    clientName?: string;
    clientPhone?: string;
    clientEmail?: string;
    clientPhone2?: string;
    clientEmail2?: string;
  };
  serviceTypeDescr?: {
    pst_descr?: string;
    pst_color?: string;
  };
  serviceCategoryDescr?: {
    psc_descr?: string;
  };
  serviceFreqTypeDescr?: {
    name?: string;
  };
  serviceCreatedUser?: {
    userFirstName?: string;
  };
  serviceUpdatedUser?: {
    userFirstName?: string;
  };
  serviceTags?: Array<{
    id: number;
    name: string;
    color: string;
  }>;
  renewalReminders?: Array<{
    id: number;
    sentAt?: string | null;
    renewalResponseStatus?: string | null;
    renewalResponseAt?: string | null;
    renewalResponseNote?: string | null;
    note?: string | null;
  }>;
}

export interface ClientServicesResponse {
  records: ClientService[];
  overdueCount?: number;
  next30DaysCount?: number;
  packageCount?: number;
  subscriptionCount?: number;
  serviceCount?: number;
}

export const useClientServices = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }

  const { data, error, isLoading, mutate } = useSWR<ClientServicesResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const services = data?.records || [];

  return {
    data: services,
    services: services,
    total: services.length,
    isLoading,
    isError: error,
    mutate,
    overdueCount: data?.overdueCount || 0,
    next30DaysCount: data?.next30DaysCount || 0,
    packageCount: data?.packageCount || 0,
    subscriptionCount: data?.subscriptionCount || 0,
    serviceCount: data?.serviceCount || 0,
  };
};

export const useClientService = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<ClientService>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    service: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();

  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get service types for select dropdown
export const useServiceTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-service-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    serviceTypes: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((type) => ({
      value: String(type.id),
      label: type.pst_descr || `Service Type #${type.id}`,
      icon: React.createElement('span', {
        className: 'inline-block h-2.5 w-2.5 rounded-full border border-black/10',
        style: { backgroundColor: parseStatusColorConfig(type.pst_color).backgroundColor },
      }),
    })),
  };
};

// Hook to get service categories for select dropdown
export const useServiceCategoriesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-service-category',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    serviceCategories: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((category) => ({
      value: String(category.id),
      label: category.psc_descr || `Category #${category.id}`,
    })),
  };
};

// Hook to get frequency types for select dropdown
export const useFrequencyTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-frequency-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    frequencyTypes: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((freq) => ({
      value: String(freq.id),
      label: freq.name || `Frequency #${freq.id}`,
    })),
  };
};

